<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessageBox } from 'element-plus';
import type { AdminPermission } from '@gyjk/shared';
import { useAuthStore } from '@/stores/auth';
import { useBrandStore } from '@/stores/brand';
import { MENU_MODULES } from '@/config/menu';
import instance, { useMock } from '@/utils/request';
import { HomeFilled, Document, UserFilled, GoodsFilled, Tickets, WalletFilled, FirstAidKit, Setting, ArrowDown, Fold, Expand, Menu } from '@element-plus/icons-vue';

const router = useRouter();
const auth = useAuthStore();
const brand = useBrandStore();
const logoStyle = computed(() => (brand.logo ? { backgroundImage: `url(${brand.logo})` } : {}));

const currentTitle = computed(() => (router.currentRoute.value.meta?.title as string) ?? '工作台');
const collapsed = ref(false);
const mobileMenuOpen = ref(false);
const isMobile = ref(false);
const iconMap: Record<string, any> = { House: HomeFilled, Document, UserFilled, Goods: GoodsFilled, List: Tickets, Money: WalletFilled, FirstAidKit, Setting };
const breadcrumbModule = computed(() => (router.currentRoute.value.meta?.module as string) || '工作台');

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
  if (!isMobile.value) mobileMenuOpen.value = false;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  void loadCurrentVersion();
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobile);
});

// 版本检查与"清空缓存":解决发版后浏览器仍展示旧页面的问题。
// 原理:部署时写入 dist/version.json(git 短 hash),点击"清空缓存"时绕过缓存拉取比对,
// 然后刷新页面——index.html 本身 no-cache,刷新即拿到最新构建。
const currentVersion = ref('');
const fetchLatestVersion = async (): Promise<string> => {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return '';
    const data = (await res.json().catch(() => ({}))) as { version?: string };
    return data?.version || '';
  } catch {
    return '';
  }
};
const loadCurrentVersion = async () => {
  currentVersion.value = await fetchLatestVersion();
};
const clearCacheAndReload = async () => {
  const latest = await fetchLatestVersion();
  const hasNew = latest && currentVersion.value && latest !== currentVersion.value;
  const message = hasNew
    ? `发现新版本(${currentVersion.value} → ${latest}),刷新后立即生效。`
    : '将刷新页面并加载最新版本。';
  try {
    await ElMessageBox.confirm(message, '清空缓存', {
      type: 'warning',
      confirmButtonText: '清空并刷新',
      cancelButtonText: '取消',
    });
    location.reload();
  } catch {
    // 用户取消,不刷新
  }
};

// 按权限过滤菜单
const visibleModules = computed(() => {
  return MENU_MODULES
    .map((m) => ({
      ...m,
      children: m.children.filter(
        (item) =>
          (useMock || item.id !== 'dashboard-switch-role') &&
          auth.hasPermission(item.permission as AdminPermission),
      ),
    }))
    .filter((m) => m.children.length > 0);
});

const handleMenuSelect = () => {
  if (isMobile.value) mobileMenuOpen.value = false;
};

const logout = async () => {
  try {
    await instance.post('/admin/auth/logout');
  } finally {
    auth.clearAuth();
    router.push('/login');
  }
};

const handleUserCommand = (command: string) => {
  if (command === 'logout') void logout();
  if (command === 'clearCache') void clearCacheAndReload();
};
</script>

<template>
  <ElContainer class="layout">
    <!-- 桌面端侧边栏 -->
    <ElAside v-if="!isMobile" :width="collapsed ? '68px' : '224px'" class="aside">
      <div class="logo">
        <div class="logo-mark" :style="logoStyle" role="img" aria-label="系统 Logo" />
        <div v-if="!collapsed" class="logo-copy"><strong>{{ brand.mainTitle }}</strong><span>{{ brand.subTitle }}</span></div>
      </div>
      <div v-if="!collapsed" class="role-pill"><span class="role-dot" />{{ auth.admin?.roleName ?? '游客' }}</div>
      <ElMenu :default-active="$route.path" :collapse="collapsed" router>
        <template v-for="module in visibleModules" :key="module.id">
          <ElSubMenu :index="module.id">
            <template #title>
              <ElIcon><component :is="iconMap[module.icon] || Document" /></ElIcon>
              <span>{{ module.title }}</span>
            </template>
            <ElMenuItem v-for="item in module.children" :key="item.id" :index="item.path" @click="handleMenuSelect">
              <span>{{ item.title }}</span>
              <ElBadge v-if="item.badge" :value="item.badge" class="badge" />
            </ElMenuItem>
          </ElSubMenu>
        </template>
      </ElMenu>
    </ElAside>

    <ElContainer>
      <ElHeader class="header">
        <div class="header-left">
          <template v-if="!isMobile">
            <button class="collapse-button" @click="collapsed = !collapsed"><ElIcon><component :is="collapsed ? Expand : Fold" /></ElIcon></button>
          </template>
          <template v-else>
            <button class="collapse-button" @click="mobileMenuOpen = true"><ElIcon><Menu /></ElIcon></button>
          </template>
          <div class="header-titles">
            <div class="breadcrumb">{{ breadcrumbModule }}</div>
            <div class="title">{{ currentTitle }}</div>
          </div>
        </div>
        <ElDropdown @command="handleUserCommand">
          <span class="user-info">
            <span class="avatar">{{ (auth.admin?.realName || '管').slice(0, 1) }}</span>
            <span v-if="!isMobile" class="user-copy">
              <strong>{{ auth.admin?.realName || '管理员' }}</strong>
              <small>{{ auth.admin?.roleName || '后台用户' }}</small>
            </span>
            <ElIcon><ArrowDown /></ElIcon>
          </span>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem>个人中心</ElDropdownItem>
              <ElDropdownItem command="clearCache">清空缓存</ElDropdownItem>
              <ElDropdownItem command="logout">退出登录</ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </ElHeader>
      <ElMain>
        <RouterView />
      </ElMain>
    </ElContainer>

    <!-- 移动端抽屉菜单 -->
    <ElDrawer v-model="mobileMenuOpen" title="菜单" direction="ltr" size="260px" class="mobile-drawer" :with-header="false">
      <div class="mobile-drawer__inner">
        <div class="logo">
          <div class="logo-mark" :style="logoStyle" role="img" aria-label="系统 Logo" />
          <div class="logo-copy"><strong>{{ brand.mainTitle }}</strong><span>{{ brand.subTitle }}</span></div>
        </div>
        <div class="role-pill"><span class="role-dot" />{{ auth.admin?.roleName ?? '游客' }}</div>
        <ElMenu :default-active="$route.path" router>
          <template v-for="module in visibleModules" :key="module.id">
            <ElSubMenu :index="module.id">
              <template #title>
                <ElIcon><component :is="iconMap[module.icon] || Document" /></ElIcon>
                <span>{{ module.title }}</span>
              </template>
              <ElMenuItem v-for="item in module.children" :key="item.id" :index="item.path" @click="handleMenuSelect">
                <span>{{ item.title }}</span>
                <ElBadge v-if="item.badge" :value="item.badge" class="badge" />
              </ElMenuItem>
            </ElSubMenu>
          </template>
        </ElMenu>
      </div>
    </ElDrawer>
  </ElContainer>
</template>

<style scoped>
.layout { height: 100vh; background: var(--gyjk-bg); }
.aside {
  background: linear-gradient(180deg, var(--gyjk-brand-dark) 0%, color-mix(in srgb, var(--gyjk-brand-dark) 82%, #000) 100%);
  color: #fff;
  overflow-y: auto;
  transition: width .22s ease;
  box-shadow: 6px 0 24px rgba(12, 47, 39, .14);
}
.logo {
  height: 64px; padding: 10px 14px; display: flex; align-items: center; gap: 10px; color: #fff; border-bottom: 1px solid rgba(255,255,255,.08);
}
.logo-mark { flex: 0 0 40px; width: 40px; height: 40px; border-radius: 8px; background:#fff url('/brand/gyjk-logo.png') center/contain no-repeat; box-shadow: 0 6px 18px rgba(0,0,0,.18); }
.logo-copy { min-width: 0; display: flex; flex-direction: column; gap: 3px; white-space: nowrap; }
.logo-copy strong { font-size: 17px; letter-spacing: 1px; }.logo-copy span { color: #a9cfc3; font-size: 11px; }
.role-pill { margin: 10px 14px 6px; padding: 7px 10px; border-radius: 8px; background: rgba(255,255,255,.07); color: #c6e3da; font-size: 12px; }
.role-dot { display:inline-block; width:7px; height:7px; margin-right:7px; border-radius:50%; background:#34d399; box-shadow:0 0 0 3px rgba(52,211,153,.13); }
.aside :deep(.el-menu) { border: 0; background: transparent; }
.aside :deep(.el-menu-item), .aside :deep(.el-sub-menu__title) { height: 42px; margin: 2px 8px; border-radius: 8px; color: #c6d9d3; }
.aside :deep(.el-menu-item:hover), .aside :deep(.el-sub-menu__title:hover) { color: #fff; background: rgba(255,255,255,.07); }
.aside :deep(.el-menu-item.is-active) { color: #fff; background: linear-gradient(90deg,rgba(16,185,129,.28),rgba(16,185,129,.12)); box-shadow: inset 3px 0 #34d399; }

.mobile-drawer :deep(.el-drawer__body) {
  padding: 0;
  background: transparent;
}
.mobile-drawer__inner {
  min-height: 100%;
  padding-bottom: 20px;
  background: linear-gradient(180deg, var(--gyjk-brand-dark) 0%, color-mix(in srgb, var(--gyjk-brand-dark) 82%, #000) 100%);
}
.mobile-drawer__inner :deep(.el-menu) {
  border: 0;
  background: transparent;
}
.mobile-drawer__inner :deep(.el-menu-item),
.mobile-drawer__inner :deep(.el-sub-menu__title) {
  height: 44px;
  margin: 2px 8px;
  border-radius: 8px;
  color: #c6d9d3;
}
.mobile-drawer__inner :deep(.el-menu-item:hover),
.mobile-drawer__inner :deep(.el-sub-menu__title:hover) {
  color: #fff;
  background: rgba(255,255,255,.07);
}
.mobile-drawer__inner :deep(.el-menu-item.is-active) {
  color: #fff;
  background: linear-gradient(90deg,rgba(16,185,129,.28),rgba(16,185,129,.12));
  box-shadow: inset 3px 0 #34d399;
}

.header {
  height: var(--admin-header-height); padding: 0 24px; display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,.92); border-bottom: 1px solid var(--gyjk-border); backdrop-filter: blur(10px);
}
.header-left { display:flex; align-items:center; gap:12px; }
.collapse-button { width:34px;height:34px;border:1px solid var(--gyjk-border);border-radius:8px;background:#fff;color:#547168;cursor:pointer;display:grid;place-items:center; }
.header-titles { display: flex; flex-direction: column; justify-content: center; }
.breadcrumb { color:#8aa098;font-size:11px;margin-bottom:3px; }
.title { color:var(--gyjk-text);font-size:17px;font-weight:700; }
.user-info { cursor: pointer; display: flex; align-items: center; gap:10px; padding:6px 9px; border-radius:11px; transition:background .2s; }
.user-info:hover{background:#f0f7f4;}
.avatar { width:32px;height:32px;border-radius:9px;display:grid;place-items:center;background:var(--gyjk-primary-soft);color:var(--gyjk-primary);font-weight:800; }
.user-copy{display:flex;flex-direction:column;min-width:72px}
.user-copy strong{font-size:13px;color:var(--gyjk-text)}
.user-copy small{font-size:11px;color:var(--gyjk-text-secondary);margin-top:2px}
.badge { margin-left: 8px; }
:deep(.el-main) { padding: 0; overflow: auto; }

@media (max-width: 768px) {
  .header { padding: 0 12px; }
  .header-titles .breadcrumb { display: none; }
  .header-titles .title { font-size: 15px; }
  .collapse-button { width: 36px; height: 36px; }
  .user-info { padding: 4px; }
  .avatar { width: 32px; height: 32px; }
}
</style>
